import { motion } from 'framer-motion';

export default function LegalNotice() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto px-4 py-12"
    >
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Mentions Légales</h1>
      <p className="text-sm text-gray-500 mb-10">Dernière mise à jour : mai 2026</p>

      <div className="space-y-10">

        {/* 1. Éditeur */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Éditeur de l'application</h2>
          <p className="text-gray-600 leading-relaxed">
            L'application <strong>OPTISECUR</strong> est éditée par :<br /><br />
            <strong>EBELMANN SAS</strong><br />
            SIRET : 94436634300019<br />
            Capital social : 1 000 €<br />
            Email : <a href="mailto:support@optisecur.com" className="text-blue-600 hover:underline">support@optisecur.com</a>
          </p>
        </section>

        {/* 2. Directeur de publication */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Directeur de publication</h2>
          <p className="text-gray-600 leading-relaxed">
            Le directeur de publication est le représentant légal d'EBELMANN SAS.<br />
            Contact : <a href="mailto:support@optisecur.com" className="text-blue-600 hover:underline">support@optisecur.com</a>
          </p>
        </section>

        {/* 3. Hébergement */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Hébergement</h2>
          <p className="text-gray-600 leading-relaxed">
            L'application est hébergée par :<br /><br />
            <strong>Netlify, Inc.</strong><br />
            44 Montgomery Street, Suite 300<br />
            San Francisco, CA 94104<br />
            États-Unis<br />
            Site : <a href="https://www.netlify.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.netlify.com</a>
          </p>
          <p className="text-gray-600 leading-relaxed mt-4">
            Les données sont hébergées sur l'infrastructure <strong>Supabase</strong> (PostgreSQL),
            dont les serveurs sont situés dans l'Union Européenne (région eu-west).
          </p>
        </section>

        {/* 4. Propriété intellectuelle */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Propriété intellectuelle</h2>
          <p className="text-gray-600 leading-relaxed">
            L'ensemble du contenu de l'application OPTISECUR (structure, textes, algorithmes de
            scoring, rapports, logos, bases de données, graphiques…) est protégé par le droit
            d'auteur et constitue la propriété exclusive d'EBELMANN SAS, sauf mention contraire.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Toute reproduction, représentation, modification, publication, adaptation ou exploitation,
            totale ou partielle, par quelque moyen ou procédé que ce soit, est strictement interdite
            sans autorisation écrite préalable d'EBELMANN SAS.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Les statistiques de criminalité affichées dans les diagnostics proviennent de l'API
            publique <strong>data.gouv.fr</strong> (Ministère de l'Intérieur) et sont réutilisées
            conformément à la licence ouverte Etalab.
          </p>
        </section>

        {/* 5. Données personnelles */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Données personnelles et RGPD</h2>
          <p className="text-gray-600 leading-relaxed">
            OPTISECUR collecte et traite des données personnelles dans le cadre de la fourniture
            du service de diagnostic de sécurité résidentielle. EBELMANN SAS agit en qualité de
            <strong> responsable du traitement</strong> au sens du Règlement Général sur la Protection
            des Données (RGPD — Règlement UE 2016/679).
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Pour toute information sur le traitement de vos données personnelles, vos droits et
            les moyens de les exercer, veuillez consulter notre{' '}
            <a href="/politique-confidentialite" className="text-blue-600 hover:underline">
              Politique de confidentialité
            </a>.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Pour exercer vos droits ou pour toute question relative à la protection de vos données :<br />
            <a href="mailto:support@optisecur.com" className="text-blue-600 hover:underline">support@optisecur.com</a>
          </p>
        </section>

        {/* 6. Cookies */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Cookies et traceurs</h2>
          <p className="text-gray-600 leading-relaxed">
            L'application utilise uniquement des <strong>cookies techniques strictement nécessaires</strong>
            au fonctionnement du service (gestion de session d'authentification). Ces cookies
            ne nécessitent pas de consentement préalable conformément à l'article 82 de la loi
            Informatique et Libertés.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Aucun cookie publicitaire, de profilage ou de suivi comportemental n'est utilisé.
          </p>
        </section>

        {/* 7. Responsabilité */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Limitation de responsabilité</h2>
          <p className="text-gray-600 leading-relaxed">
            Les diagnostics et recommandations fournis par OPTISECUR ont une valeur indicative et
            ne se substituent pas à l'expertise d'un professionnel de la sécurité. EBELMANN SAS
            ne saurait être tenu responsable des décisions prises sur la base des résultats fournis
            par l'application.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            EBELMANN SAS ne saurait garantir l'absence d'interruptions ou d'erreurs dans le
            fonctionnement de l'application, ni l'exactitude absolue des statistiques de
            délinquance issues de sources tierces.
          </p>
        </section>

        {/* 8. Liens hypertextes */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Liens hypertextes</h2>
          <p className="text-gray-600 leading-relaxed">
            L'application peut contenir des liens vers des sites tiers. EBELMANN SAS n'exerce
            aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu,
            leurs pratiques en matière de données personnelles et leur disponibilité.
          </p>
        </section>

        {/* 9. Droit applicable */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Droit applicable et juridiction</h2>
          <p className="text-gray-600 leading-relaxed">
            Les présentes mentions légales sont régies par le droit français. En cas de litige
            relatif à l'utilisation de l'application, et après tentative de résolution amiable,
            les tribunaux français seront seuls compétents.
          </p>
        </section>

        {/* 10. Modification */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Modification des présentes mentions</h2>
          <p className="text-gray-600 leading-relaxed">
            EBELMANN SAS se réserve le droit de modifier les présentes mentions légales à tout
            moment, notamment pour se conformer à toute nouvelle réglementation applicable.
            La date de dernière mise à jour est indiquée en haut de cette page.
          </p>
        </section>

      </div>
    </motion.div>
  );
}
